import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';
import Colors from '../../../../ui/global/Colors';
import Fonts from '../../../../ui/global/Fonts';
import ArtAsset from './ArtAsset';
import { useArtList, Art } from '../../../../backend/useArtList';
import { useUser } from '../../../../backend/useUser';
import LoadingSpinner from '../../../../ui/components/LoadingSpinner';
import Button from '../../../../ui/components/Button';
import Tooltip from '../../../../ui/components/Tooltip';
import MenuOpen from '../../../../ui/components/MenuOpen';

const Content = styled.div<{ $height: string }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    max-height: ${props => props.$height};
    line-height: 1.2em;
    padding: 4px 0;
    gap: 10px;
    z-index: 100;
`;

const TitleContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0 7px 0 12px;
    margin: 0 auto -5px auto;
    gap: 5px;
`;

const Title = styled.h4`
    text-align: left;
    font-family: ${Fonts.lexendSemiBold.fontFamily};
    font-weight: ${Fonts.lexendSemiBold.fontWeight};
    font-size: 16px;
    color: ${Colors.grey1};
    white-space: nowrap;
    padding: 0 10px 0px 4px;
    margin: 0;
    z-index: 10;
`;

const Inner = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    height: 100%;
    line-height: 1.2em;
    padding: 5px 10px 10px 10px;
    gap: 10px;
    overflow-x: hidden;
    overflow-y: auto;

    /* Firefox scrollbar */
    scrollbar-width: thin;
    scrollbar-color: ${Colors.grey7} ${Colors.grey9};
    
    /* Webkit scrollbar (Chrome, Safari, Edge) */
    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: ${Colors.grey9};
    }
    &::-webkit-scrollbar-thumb {
      background: ${Colors.grey7};
      border-radius: 4px;
    }
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    width: 100%;
    height: 100%;
    gap: 12px;
    
    @media (min-width: 480px) {
        grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    }
    
    @media (min-width: 768px) {
        grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    }
`;

const LoadingRef = styled.div`
    position: relative;
    height: 100px;
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 10px;
    // border: 1px solid red;
`;

const Together = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 2px;
    // margin: auto;
`;

interface ArtMenuMiniProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    selectedArtIndex?: number | null;
    onArtSelected: (artId: string, aiImage: string, artName: string) => void;
    actionsButtonRef: React.RefObject<HTMLDivElement>;
    filterText?: string;
    height?: string;
    placement?: 'top-start' | 'bottom-start' | 'left-start' | 'right-start';
}

const ITEMS_PER_PAGE = 16;

const ArtMenuMini: React.FC<ArtMenuMiniProps> = ({ isOpen, onOpenChange, selectedArtIndex, onArtSelected, actionsButtonRef, filterText = '', height = '84vh', placement = 'top-start' }) => {
  const { listMyArt2D } = useArtList();
  const { user, loading: userLoading } = useUser();
  const [myArt, setMyArt] = useState<Art[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(null);

  const loadArt = useCallback(async (pageNum: number) => {
    if (userLoading || !user) return;

    setLoading(true);
    try {
      const { arts, hasMore: more } = await listMyArt2D(user.id, pageNum, ITEMS_PER_PAGE, true);
      setMyArt(prev => pageNum === 1 ? arts : [...prev, ...arts]);
      setHasMore(more);
    } catch (error) {
      console.error('Error loading art:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [listMyArt2D, user, userLoading]);

  // useEffect(() => {
  //   console.log('anchorEl', actionsButtonRef.current);
  // }, [actionsButtonRef]);

  // useEffect(() => {
  //   console.log('myArt', myArt.length);
  // }, [myArt]);

  // useEffect(() => {
  //   console.log('hasMore', hasMore);
  // }, [hasMore]);

  useEffect(() => {
    if (user) {
      setPage(1);
      loadArt(1);
    }
  }, [loadArt, user]);

  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore && isOpen && actionsButtonRef?.current) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadArt(nextPage);
      // console.log('Loading more art...', page);
    }
  }, [loading, hasMore, page, loadArt, isOpen, actionsButtonRef]);

  // Automatically load the next page when the current page finishes loading
  useEffect(() => {
    // Only trigger when loading changes from true to false
    // and there are more pages to load
    if (!loading && hasMore) {
      // Small delay to prevent rapid consecutive requests
      const timer = setTimeout(() => {
        handleLoadMore();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [loading, hasMore, handleLoadMore]);


  const handleUploadImage = () => {
    // console.log('Upload image clicked');
    // TODO: Implement upload image functionality
  };

  const handleCreateNewArt = () => {
    // console.log('Create new art clicked');
    // TODO: Implement create new art functionality
  };

  const handleSearch = () => {
    // console.log('Search clicked');
    // TODO: Implement search functionality
  };

  const handleClose = () => {
      onOpenChange(false);
  };

  // Filter art items based on filterText
  const filteredArt = myArt.filter(art => {
    if (filterText === '') return true;
    const searchText = filterText.toLowerCase();
    return art.art_name.toLowerCase().includes(searchText);
  });

  // Only show the menu if there are art items to display after filtering
  const shouldShowMenu = isOpen && !!actionsButtonRef.current && (userLoading || filteredArt.length > 0);

  return (
    <>
    {shouldShowMenu && (
      <MenuOpen
          anchorEl={actionsButtonRef.current || undefined}
          open={shouldShowMenu}
          onClose={handleClose}
          color="black"
          minWidth={140}
          placement={placement}
          children={
            <Content $height={height}>
              <TitleContainer>
                  <Together>
                    <Title>My Art</Title>
                    <Tooltip text="Create New Art" colorMode="dark" >
                        <Button
                            size="tiny"
                            color="ghost white" 
                            icon="plus" 
                            onClick={handleCreateNewArt} 
                        />
                    </Tooltip>
                    <Tooltip text="Upload Image" colorMode="dark" >
                        <Button
                            size="tiny"
                            color="ghost white" 
                            icon="upload" 
                            onClick={handleUploadImage} 
                        />
                    </Tooltip>
                    <Tooltip text="Search" colorMode="dark" >
                        <Button
                            size="tiny"
                            color="ghost white" 
                            icon="search" 
                            onClick={handleSearch} 
                        />
                    </Tooltip>
                  </Together>
                  <Tooltip text="Close" colorMode="dark" >
                      <Button
                          size="tiny"
                          color="ghost white" 
                          icon="delete" 
                          onClick={() => onOpenChange(false)} 
                      />
                  </Tooltip>
              </TitleContainer>
              <Inner>
                {userLoading ? (
                  <LoadingSpinner colorMode='dark' />
                ) : filteredArt.length > 0 ? (
                    <Grid>
                      {filteredArt.map((art, index) => (
                        <ArtAsset 
                          key={art.id} 
                          art={{
                            name: art.art_name,
                            image: art.aiImage || art.userPng || ''
                          }}
                          onClick={() => onArtSelected(art.id, art.aiImage, art.art_name)}
                          isDisabled={false}
                        />
                      ))}
                    </Grid>
                ) : (
                  <></>
                )}
                { loading && (
                    <LoadingSpinner colorMode='dark' />
                )}
                {hasMore && (
                    <LoadingRef ref={loadingRef} />
                )}
              </Inner>
          </Content>
          }
      />
  )}
  </>
  );
};

export default ArtMenuMini;